import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, logout } = useAuth();

  const isAdmin = role === 'SUPER_ADMIN' || role === 'ADMIN';
  const isAcarya = role === 'ACARYA';
  const isVendor = role === 'VENDOR';
  // CENTRE role = verified centre institution (manages own centres, live streams, ceremonies, etc.)
  const isCentre = role === 'CENTRE';

  const portalName = isAdmin ? 'CENTRE DASHBOARD' : role === 'ACARYA' ? 'ACARYA PORTAL' : isCentre ? 'CENTRE PORTAL' : `${role || 'VENDOR'} PORTAL`;

  const hasModule = (moduleName) => {
    if (isAdmin) return true;
    return user?.modules?.includes(moduleName);
  };

  const hasPermission = (perm) => {
    if (isAdmin) return true;
    return user?.permissions?.includes(perm);
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate('/login');
  };

  const getLinkClass = (path) => {
    return isActive(path)
      ? "flex items-center gap-3 px-6 py-3.5 text-white font-bold bg-[#2E2A5C]/40 border-l-[3px] border-[#F0C75E] transition-all"
      : "flex items-center gap-3 px-6 py-3.5 text-stone-400 hover:text-white hover:bg-white/5 transition-all group";
  };

  const getIconClass = (path) => isActive(path) ? "text-[#F0C75E]" : "text-stone-500 group-hover:text-[#F0C75E] transition-colors";

  return (
    <aside className="bg-[#0E0E26] w-64 flex flex-col fixed left-0 top-0 h-full z-40 border-r border-white/5 overflow-hidden shadow-2xl">
      {/* Background Decorative Mandala (Subtle) */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none flex items-center justify-center">
        <span className="material-symbols-outlined text-[400px]">filter_vintage</span>
      </div>

      <Link to="/dashboard" className="px-6 py-8 flex items-center gap-4 relative z-10 border-b border-white/5 mb-2">
        {/* Premium Centre Logo (Image-based for pixel-perfect fidelity) */}
        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
          <img
            src="/premium-centre-logo.png"
            alt="Ananda Marga"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        <div className="flex flex-col justify-center">
          <h1 className="text-[24px] text-white tracking-tight leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>Ananda Marga</h1>
          <p className="text-[8px] text-[#F0C75E] font-bold uppercase tracking-[0.4em] mt-0.5 leading-none">
            {portalName === 'Admin Dashboard' ? 'CENTRE DASHBOARD' : portalName.toUpperCase()}
          </p>
        </div>
      </Link>

      <nav className="flex-1 space-y-1 overflow-y-auto font-serif text-[11px] tracking-wide custom-scrollbar relative z-10 pb-20">
        {/* --- COMMON DASHBOARD --- */}
        <Link to="/dashboard" className={getLinkClass('/dashboard')}>
          <span className={`material-symbols-outlined text-xl ${getIconClass('/dashboard')}`} style={isActive('/dashboard') ? { fontVariationSettings: "'FILL' 1" } : {}}>dashboard</span>
          Dashboard
        </Link>

        {/* Deep real-time platform analytics — ADMIN only */}
        {isAdmin && (
          <Link to="/analytics" className={getLinkClass('/analytics')}>
            <span className={`material-symbols-outlined text-xl ${getIconClass('/analytics')}`} style={isActive('/analytics') ? { fontVariationSettings: "'FILL' 1" } : {}}>monitoring</span>
            Analytics
          </Link>
        )}

        {/* ===================================================== */}
        {/* CENTRE PORTAL — Focused, institutional workspace only  */}
        {/* ===================================================== */}
        {isCentre && (
          <>
            <div className="pt-6 pb-2 px-6 text-[9px] font-black text-white/20 uppercase tracking-[0.2em]">Financial Ledger</div>
            <Link to="/orders" className={getLinkClass('/orders')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/orders')}`}>receipt_long</span>
              Orders
            </Link>
            <Link to="/donations" className={getLinkClass('/donations')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/donations')}`}>volunteer_activism</span>
              Donations
            </Link>
            <Link to="/offerings" className={getLinkClass('/offerings')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/offerings')}`}>auto_awesome</span>
              Offerings
            </Link>
            <Link to="/centre/revenue" className={getLinkClass('/centre/revenue')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/centre/revenue')}`}>payments</span>
              Total Revenue
            </Link>
          </>
        )}


        {/* ===================================================== */}
        {/* ADMIN — Full platform sidebar                          */}
        {/* ===================================================== */}
        {isAdmin && (
          <>
            <Link to="/centres" className={getLinkClass('/centres')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/centres')}`}>account_balance</span>
              Centres
            </Link>
            <Link to="/live-streams" className={getLinkClass('/live-streams')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/live-streams')}`}>sensors</span>
              Live Streams
            </Link>

            {(hasModule('ceremonies') || hasPermission('Ceremony Services')) && (
              <Link to="/ceremonies" className={getLinkClass('/ceremonies')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/ceremonies')}`}>auto_awesome</span>
                Ceremony Services
              </Link>
            )}
            {(hasModule('sevaOffering') || hasPermission('SevaOffering')) && (
              <Link to="/sevaOffering" className={getLinkClass('/sevaOffering')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/sevaOffering')}`}>volunteer_activism</span>
                SevaOffering
              </Link>
            )}
            {(hasModule('products') || hasPermission('Products')) && (
              <Link to="/products" className={getLinkClass('/products')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/products')}`}>inventory_2</span>
                Products
              </Link>
            )}
            {(hasModule('products') || hasPermission('Products')) && (
              <Link to="/centre-shop" className={getLinkClass('/centre-shop')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/centre-shop')}`}>storefront</span>
                Centre Shop
              </Link>
            )}
            <Link to="/orders" className={getLinkClass('/orders')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/orders')}`}>shopping_cart</span>
              Orders
            </Link>
            {(hasModule('donations') || hasPermission('Donations')) && (
              <Link to="/donations" className={getLinkClass('/donations')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/donations')}`}>payments</span>
                Donations
              </Link>
            )}
            {hasPermission('Notifications') && (
              <Link to="/notifications" className={getLinkClass('/notifications')}>
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <span className={`material-symbols-outlined text-xl ${getIconClass('/notifications')}`}>campaign</span>
                    Notifications
                  </div>
                  <span className="bg-red-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full">8</span>
                </div>
              </Link>
            )}
            {hasPermission('Subscriptions') && (
              <Link to="/subscriptions" className={getLinkClass('/subscriptions')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/subscriptions')}`}>card_membership</span>
                Subscriptions
              </Link>
            )}

            <div className="pt-6 pb-2 px-6 text-[9px] font-black text-white/20 uppercase tracking-[0.2em]">Platform Management</div>
            <Link to="/centre-requests" className={getLinkClass('/centre-requests')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/centre-requests')}`}>domain_add</span>
              Centre Requests
            </Link>
            <Link to="/vendor-requests" className={getLinkClass('/vendor-requests')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/vendor-requests')}`}>pending_actions</span>
              Vendor Requests
            </Link>
            <Link to="/acarya-requests" className={getLinkClass('/acarya-requests')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/acarya-requests')}`}>person_search</span>
              Acarya Requests
            </Link>
            <Link to="/vendors" className={getLinkClass('/vendors')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/vendors')}`}>storefront</span>
              Vendors
            </Link>
            <Link to="/admin/payouts" className={getLinkClass('/admin/payouts')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/admin/payouts')}`}>account_balance</span>
              Payouts Requests
            </Link>
            <Link to="/categories" className={getLinkClass('/categories')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/categories')}`}>category</span>
              Categories
            </Link>
            <Link to="/events" className={getLinkClass('/events')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/events')}`}>event</span>
              Events
            </Link>
            <Link to="/seva-reports" className={getLinkClass('/seva-reports')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/seva-reports')}`}>volunteer_activism</span>
              Seva Reports
            </Link>
            <Link to="/ebooks" className={getLinkClass('/ebooks')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/ebooks')}`}>auto_stories</span>
              E-Books
            </Link>
            <Link to="/audio-library" className={getLinkClass('/audio-library')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/audio-library')}`}>library_music</span>
              Audio Library
            </Link>
            <Link to="/users" className={getLinkClass('/users')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/users')}`}>group</span>
              Users
            </Link>
            <Link to="/delete-account-requests" className={getLinkClass('/delete-account-requests')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/delete-account-requests')}`}>person_remove</span>
              Delete Requests
            </Link>
            <Link to="/pages" className={getLinkClass('/pages')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/pages')}`}>article</span>
              Pages
            </Link>
            <Link to="/hero" className={getLinkClass('/hero')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/hero')}`}>view_carousel</span>
              Hero Slider
            </Link>
            <Link to="/reels" className={getLinkClass('/reels')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/reels')}`}>smart_display</span>
              Reels Studio
            </Link>
            <Link to="/acaryas" className={getLinkClass('/acaryas')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/acaryas')}`}>psychology</span>
              Acaryas
            </Link>
            <Link to="/settings" className={getLinkClass('/settings')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/settings')}`}>admin_panel_settings</span>
              Platform Settings
            </Link>
          </>
        )}

        {/* ===================================================== */}
        {/* VENDOR — Commerce focused sidebar                      */}
        {/* ===================================================== */}
        {isVendor && (
          <>
            {(hasModule('ceremonies') || hasPermission('Ceremony Services')) && (
              <Link to="/ceremonies" className={getLinkClass('/ceremonies')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/ceremonies')}`}>auto_awesome</span>
                Ceremony Services
              </Link>
            )}
            {(hasModule('sevaOffering') || hasPermission('SevaOffering')) && (
              <Link to="/sevaOffering" className={getLinkClass('/sevaOffering')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/sevaOffering')}`}>volunteer_activism</span>
                SevaOffering
              </Link>
            )}
            {(hasModule('products') || hasPermission('Products')) && (
              <Link to="/products" className={getLinkClass('/products')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/products')}`}>inventory_2</span>
                Products
              </Link>
            )}
            {(['products', 'ceremonies', 'sevaOffering'].some(m => hasModule(m)) || hasPermission('Orders')) && (
              <Link to="/orders" className={getLinkClass('/orders')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/orders')}`}>shopping_cart</span>
                Orders
              </Link>
            )}
            {(user?.modules?.some(m => ['products', 'ceremonies', 'sevaOffering', 'donations'].includes(m))) && (
              <Link to="/wallet" className={getLinkClass('/wallet')}>
                <span className={`material-symbols-outlined text-xl ${getIconClass('/wallet')}`}>account_balance_wallet</span>
                My Wallet
              </Link>
            )}
          </>
        )}

        {/* --- ACARYA ONLY --- */}
        {isAcarya && (
          <>
            <div className="pt-6 pb-2 px-6 text-[9px] font-black text-white/20 uppercase tracking-[0.2em]">Consultant Portal</div>
            <Link to="/consultations/inbox" className={getLinkClass('/consultations/inbox')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/consultations/inbox')}`}>forum</span>
              Consultation Box
            </Link>
            <Link to="/acarya/revenue" className={getLinkClass('/acarya/revenue')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/acarya/revenue')}`}>payments</span>
              Revenue
            </Link>
            <Link to="/wallet" className={getLinkClass('/wallet')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/wallet')}`}>account_balance_wallet</span>
              My Wallet
            </Link>
            <Link to="/acarya/slots" className={getLinkClass('/acarya/slots')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/acarya/slots')}`}>calendar_month</span>
              Slot Management
            </Link>
            <Link to="/acarya/profile" className={getLinkClass('/acarya/profile')}>
              <span className={`material-symbols-outlined text-xl ${getIconClass('/acarya/profile')}`}>person</span>
              Profile
            </Link>
          </>
        )}
      </nav>

      {/* --- CENTRE GRAPHIC (PIXEL PERFECT REPLICATION) --- */}
      <div className="relative h-48 mt-auto flex items-end justify-center px-6 pb-2 opacity-10 pointer-events-none">
        <svg viewBox="0 0 200 200" className="w-full h-full text-[#F0C75E]" fill="currentColor">
          <path d="M100 20 L110 50 L140 50 L115 70 L125 100 L100 80 L75 100 L85 70 L60 50 L90 50 Z" opacity="0.5" />
          <rect x="80" y="100" width="40" height="60" />
          <path d="M70 160 L130 160 L135 180 L65 180 Z" />
          <path d="M40 180 L160 180 L165 195 L35 195 Z" />
          <circle cx="100" cy="90" r="5" />
          <path d="M90 100 L110 100 L115 120 L85 120 Z" opacity="0.8" />
          <path d="M85 120 L115 120 L120 140 L80 140 Z" opacity="0.6" />
        </svg>
      </div>

      {/* --- LOGOUT SECTION --- */}
      <div className="p-6 border-t border-white/5 relative z-10">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-stone-500 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest"
        >
          <span className="material-symbols-outlined text-lg">logout</span>
          Logout
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;